import { cleanParentIds } from '@/lib/graphIdSafety';
export type ModernFamilyGovernorVerdict = {
  ok: boolean;
  stage: string;
  relationship?: string | null;
  customLabel?: string | null;
  viewerMemberId?: string | null;
  connectorId?: string | null;
  secondConnectorId?: string | null;
  parentIds?: string[];
  reason?: string;
  severity: 'info' | 'warning' | 'fail';
};

const MODERN_RELATIONSHIPS = new Set([
  'co-parent',
  'coparent',
  'step-parent',
  'adopted-parent',
  'spouse',
  'partner',
  'child',
  'sibling',
  'parent',
  'mother',
  'father',
  'unknown',
]);

export function validateModernFamilyRelationship(input: {
  stage: string;
  relationship?: string | null;
  customLabel?: string | null;
  viewerMemberId?: string | null;
  connectorId?: string | null;
  secondConnectorId?: string | null;
  parentIds?: string[] | null;
  candidateCount?: number | null;
}): ModernFamilyGovernorVerdict {
  const rel = (input.relationship || '').trim().toLowerCase();
  const label = (input.customLabel || '').trim().toLowerCase();
  const parentIds = Array.isArray(input.parentIds) ? cleanParentIds(input.parentIds) : [];

  const isModern =
    MODERN_RELATIONSHIPS.has(rel) ||
    label.includes('in-law') ||
    label.includes('step') ||
    label.includes('co-parent') ||
    label.includes('coparent') ||
    label.includes('chosen');

  if (!input.viewerMemberId) {
    return {
      ok: false,
      stage: input.stage,
      relationship: input.relationship,
      customLabel: input.customLabel,
      viewerMemberId: input.viewerMemberId || null,
      connectorId: input.connectorId || null,
      secondConnectorId: input.secondConnectorId || null,
      parentIds,
      reason: 'viewerMemberId missing before relationship mutation. This can create stale anchor, wrong branch, or disconnected placement.',
      severity: 'fail',
    };
  }

  if (isModern && rel === 'co-parent' && !input.connectorId) {
    return {
      ok: false,
      stage: input.stage,
      relationship: input.relationship,
      customLabel: input.customLabel,
      viewerMemberId: input.viewerMemberId,
      connectorId: input.connectorId || null,
      secondConnectorId: input.secondConnectorId || null,
      parentIds,
      reason: 'co-parent relationship requires a selected child connector before DB write.',
      severity: 'fail',
    };
  }

  if ((rel === 'step-parent' || rel === 'adopted-parent') && !input.connectorId) {
    return {
      ok: false,
      stage: input.stage,
      relationship: input.relationship,
      customLabel: input.customLabel,
      viewerMemberId: input.viewerMemberId,
      connectorId: input.connectorId || null,
      secondConnectorId: input.secondConnectorId || null,
      parentIds,
      reason: 'step/adopted parent requires a child anchor connector so the graph does not create an orphan parent.',
      severity: 'fail',
    };
  }

  if (isModern && input.candidateCount === 0 && !input.connectorId) {
    return {
      ok: true,
      stage: input.stage,
      relationship: input.relationship,
      customLabel: input.customLabel,
      viewerMemberId: input.viewerMemberId,
      connectorId: input.connectorId || null,
      secondConnectorId: input.secondConnectorId || null,
      parentIds,
      reason: 'modern relationship has no connector candidates yet; allowed only if saved as unconnected/pending connection.',
      severity: 'warning',
    };
  }

  return {
    ok: true,
    stage: input.stage,
    relationship: input.relationship,
    customLabel: input.customLabel,
    viewerMemberId: input.viewerMemberId,
    connectorId: input.connectorId || null,
    secondConnectorId: input.secondConnectorId || null,
    parentIds,
    reason: 'relationship mutation passed Governor validator.',
    severity: 'info',
  };
}
